import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerChat, useVoiceCall } from '../../hooks/useChat';

// ─── Announcement system ───────────────────────────────────────────────────
const I18N_TO_SPEECH_LANG: Record<string, string> = {
  tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', it: 'it-IT',
};

function getSpeechLang(i18nLang: string): string {
  const base = i18nLang.split('-')[0];
  return I18N_TO_SPEECH_LANG[base] || 'en-US';
}

function speakAnnouncement(text: string, lang: string): void {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = lang;
  utt.rate = 0.88;
  utt.pitch = 1.05;
  utt.volume = 1;
  const setVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const match =
      voices.find(v => v.lang === lang) ||
      voices.find(v => v.lang.startsWith(lang.split('-')[0]));
    if (match) utt.voice = match;
  };
  if (window.speechSynthesis.getVoices().length > 0) setVoice();
  else window.speechSynthesis.onvoiceschanged = setVoice;
  window.speechSynthesis.speak(utt);
}

// Ring sound helper
function playRingSound(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioContextClass();
    [0, 0.6, 1.2, 1.8].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.4);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.45);
    });
  } catch { /* ignore */ }
}
// ──────────────────────────────────────────────────────────────────────────

interface LiveChatWidgetProps {
  onOpen?: () => void;
  onClose?: () => void;
}

export default function LiveChatWidget({ onOpen, onClose }: LiveChatWidgetProps) {
  const { t, i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'intro' | 'chat'>('intro');
  const [nameInput, setNameInput] = useState('');
  const [convId, setConvId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [unreadBadge, setUnreadBadge] = useState(0);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callMinimized, setCallMinimized] = useState(false);
  const [lastAdminMsgCount, setLastAdminMsgCount] = useState(0);
  const [adminCallIncoming, setAdminCallIncoming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevCallStateRef = useRef<string | undefined>('idle');
  const prevAdminCallStateRef = useRef<string | undefined>('idle');

  const { conversation, createConversation, sendMessage } = useCustomerChat(convId);
  const { callState, initiate: initiateCall, acceptFromAdmin, hangup } = useVoiceCall(convId, 'customer');

  // Listen for global open event
  useEffect(() => {
    const handler = () => { setIsOpen(true); setUnreadBadge(0); };
    window.addEventListener('open-live-chat', handler);
    return () => window.removeEventListener('open-live-chat', handler);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages.length]);

  // Unread badge for admin messages when closed
  useEffect(() => {
    if (!conversation) return;
    const adminMsgs = conversation.messages.filter(m => m.sender === 'admin').length;
    if (adminMsgs > lastAdminMsgCount) {
      if (!isOpen) setUnreadBadge(prev => prev + (adminMsgs - lastAdminMsgCount));
      setLastAdminMsgCount(adminMsgs);
    }
  }, [conversation, isOpen, lastAdminMsgCount]);

  // Watch for admin-initiated call
  useEffect(() => {
    if (!conversation) return;
    const current = conversation.callState;
    if (current === 'admin-calling' && prevAdminCallStateRef.current !== 'admin-calling') {
      setAdminCallIncoming(true);
      playRingSound();
      setIsOpen(true);
      setUnreadBadge(0);
    }
    if (current !== 'admin-calling') {
      setAdminCallIncoming(false);
    }
    prevAdminCallStateRef.current = current;
  }, [conversation?.callState]);

  // Watch for 'busy' state — play busy announcement and reset call
  useEffect(() => {
    if (!conversation) return;
    const current = conversation.callState;
    if (current === 'busy' && prevCallStateRef.current !== 'busy') {
      const lang = getSpeechLang(i18n.language);
      speakAnnouncement(t('liveChat.busyAnnouncement'), lang);
      hangup();
      setCallModalOpen(false);
      setCallMinimized(false);
    }
    prevCallStateRef.current = current;
  }, [conversation?.callState, hangup, t, i18n.language]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setUnreadBadge(0);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleStart = useCallback(() => {
    if (!nameInput.trim()) return;
    const id = createConversation(nameInput.trim());
    setConvId(id);
    setStep('chat');
  }, [nameInput, createConversation]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan büyük olamaz.');
      return;
    }
    setUploadName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSend = useCallback(() => {
    const text = messageText.trim();
    if (!text && !uploadPreview) return;
    sendMessage(text || '📎 Dosya gönderildi', uploadPreview || undefined);
    setMessageText('');
    setUploadPreview(null);
    setUploadName('');
  }, [messageText, uploadPreview, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceCall = () => {
    const lang = getSpeechLang(i18n.language);
    speakAnnouncement(t('liveChat.welcomeAnnouncement'), lang);
    setCallModalOpen(true);
    initiateCall();
  };

  const handleHangup = () => {
    window.speechSynthesis?.cancel();
    hangup();
    setCallModalOpen(false);
    setCallMinimized(false);
  };

  const handleAcceptAdminCall = useCallback(async () => {
    setAdminCallIncoming(false);
    await acceptFromAdmin();
  }, [acceptFromAdmin]);

  const handleRejectAdminCall = useCallback(() => {
    setAdminCallIncoming(false);
    hangup();
  }, [hangup]);

  const isBusy = conversation?.callState === 'busy';

  const callStateLabel =
    isBusy ? t('liveChat.callBusy')
    : callState === 'calling' ? t('liveChat.callConnecting')
    : callState === 'connected' ? t('liveChat.callConnected')
    : t('liveChat.callEnded');

  return (
    <>
      {/* Incoming admin call notification */}
      {adminCallIncoming && callState === 'idle' && (
        <div className="fixed bottom-6 left-6 z-[400] w-72 bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 48px rgba(0,0,0,0.25)' }}>
          <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-3 flex items-center gap-2.5">
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-phone-incoming-fill text-white text-lg"></i>
              </div>
              <span className="absolute inset-0 rounded-full border-2 border-white/60 animate-ping"></span>
            </div>
            <div>
              <p className="text-xs font-bold text-white">{t('liveChat.incomingCallTitle')}</p>
              <p className="text-[10px] text-green-100">{t('liveChat.incomingCallLine')}</p>
            </div>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-600 mb-3">{t('liveChat.incomingCallMsg')}</p>
            <div className="flex gap-2">
              <button onClick={handleAcceptAdminCall} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-sm"></i>
                {t('liveChat.accept')}
              </button>
              <button onClick={handleRejectAdminCall} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-sm" style={{ transform: 'rotate(135deg)', display: 'inline-block' }}></i>
                {t('liveChat.reject')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active admin call indicator */}
      {callState === 'connected' && !callModalOpen && (
        <div className="fixed bottom-6 left-6 z-[350] bg-green-600 text-white rounded-2xl px-4 py-3 flex items-center gap-3" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
          <div className="flex items-end gap-[2px]">
            {[2, 4, 6, 4, 2].map((h, i) => (
              <div key={i} className="w-[2px] bg-white rounded-full" style={{ height: `${h}px`, animation: `liveBar 0.5s ease-in-out infinite ${i * 0.1}s alternate` }}></div>
            ))}
          </div>
          <div>
            <p className="text-xs font-bold">{t('liveChat.activeCallTitle')}</p>
            <p className="text-[10px] text-green-200">{t('liveChat.activeCallSub')}</p>
          </div>
          <button onClick={() => { window.speechSynthesis?.cancel(); hangup(); }} className="ml-1 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center cursor-pointer transition-colors" title={t('liveChat.callClose')}>
            <i className="ri-phone-fill text-xs" style={{ transform: 'rotate(135deg)' }}></i>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 left-6 z-[200] flex flex-col bg-white rounded-2xl overflow-hidden" style={{ width: '360px', maxWidth: 'calc(100vw - 24px)', height: '520px', maxHeight: 'calc(100vh - 80px)', boxShadow: '0 8px 48px rgba(0,0,0,0.2)' }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-900 text-white flex-shrink-0">
            <div className="relative w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-lg text-white"></i>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-gray-900"></span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">{t('liveChat.title')}</p>
              <p className="text-[10px] text-green-400 font-medium">{t('liveChat.online')}</p>
            </div>
            <div className="flex items-center gap-1">
              {step === 'chat' && (
                <button onClick={handleVoiceCall} title={t('liveChat.voiceCall')} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-base text-white"></i>
                </button>
              )}
              <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Intro Step */}
          {step === 'intro' && (
            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-customer-service-2-fill text-3xl text-amber-600"></i>
              </div>
              <div className="text-center">
                <p className="text-base font-bold text-gray-900 mb-1">{t('liveChat.hello')}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{t('liveChat.introDesc')}</p>
              </div>
              <div className="w-full flex flex-col gap-3">
                <input type="text" value={nameInput} onChange={e => setNameInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleStart()} placeholder={t('liveChat.namePlaceholder')} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={40} />
                <button onClick={handleStart} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
                  {t('liveChat.startChat')}
                </button>
                <button onClick={() => { setNameInput('Misafir'); setConvId(createConversation('Misafir')); setStep('chat'); }} className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  {t('liveChat.continueAsGuest')}
                </button>
              </div>
            </div>
          )}

          {/* Chat Step */}
          {step === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ minHeight: 0 }}>
                <div className="flex gap-2 items-end">
                  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-white" style={{ fontSize: '10px' }}></i>
                  </div>
                  <div className="max-w-[78%] bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {t('liveChat.hello').replace('!', '')} <strong>{conversation?.customerName}</strong>! {t('liveChat.welcomeMsg')}
                    </p>
                    <p className="text-[9px] text-gray-400 mt-1">{t('liveChat.supportTeam')}</p>
                  </div>
                </div>

                {(conversation?.messages || []).map(msg => (
                  <div key={msg.id} className={`flex gap-2 items-end ${msg.sender === 'customer' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${msg.sender === 'admin' ? 'bg-amber-500' : 'bg-gray-700'}`}>
                      <i className={`${msg.sender === 'admin' ? 'ri-customer-service-2-line' : 'ri-user-line'} text-white`} style={{ fontSize: '10px' }}></i>
                    </div>
                    <div className={`max-w-[78%] ${msg.sender === 'customer' ? 'bg-gray-900 text-white rounded-2xl rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm'} px-3 py-2`}>
                      {msg.imageUrl && <img src={msg.imageUrl} alt="Ek" className="w-full rounded-lg mb-1.5 max-h-32 object-cover cursor-pointer" onClick={() => window.open(msg.imageUrl, '_blank')} />}
                      {msg.text && <p className="text-xs leading-relaxed">{msg.text}</p>}
                      <p className="text-[9px] mt-1 text-gray-400">{new Date(msg.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}

                {/* Admin typing indicator */}
                {conversation?.adminTyping && (
                  <div className="flex gap-2 items-end">
                    <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-2-line text-white" style={{ fontSize: '10px' }}></i>
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2.5 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" style={{ animation: 'typingDot 1.2s ease-in-out infinite 0s' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" style={{ animation: 'typingDot 1.2s ease-in-out infinite 0.2s' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" style={{ animation: 'typingDot 1.2s ease-in-out infinite 0.4s' }}></span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {uploadPreview && (
                <div className="px-3 pb-2 flex items-center gap-2 bg-amber-50 border-t border-amber-100">
                  <img src={uploadPreview} alt="Önizleme" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  <span className="text-xs text-gray-600 truncate flex-1">{uploadName}</span>
                  <button onClick={() => { setUploadPreview(null); setUploadName(''); }} className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0">
                    <i className="ri-close-line text-xs text-gray-600"></i>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 flex-shrink-0">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf,.doc,.docx" onChange={handleFileChange} />
                <button onClick={() => fileInputRef.current?.click()} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-attachment-2 text-base"></i>
                </button>
                <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} onKeyDown={handleKeyDown} placeholder={t('liveChat.messagePlaceholder')} className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-amber-400 transition-colors" style={{ minWidth: 0 }} />
                <button onClick={handleSend} disabled={!messageText.trim() && !uploadPreview} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0">
                  <i className="ri-send-plane-fill text-sm"></i>
                </button>
              </div>
              <p className="text-[9px] text-gray-400 text-center pb-2">{t('liveChat.secureNote')}</p>
            </>
          )}
        </div>
      )}

      {/* Voice Call — Floating Card (expanded) */}
      {callModalOpen && !callMinimized && (
        <div className="fixed z-[300] bg-white rounded-2xl overflow-hidden" style={{ bottom: '96px', left: '50%', transform: 'translateX(-50%)', width: '290px', boxShadow: '0 12px 48px rgba(0,0,0,0.22)', animation: 'slideUpCall 0.25s ease' }}>
          <div className="flex items-center px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 flex-1">
              <div className={`w-2 h-2 rounded-full ${callState === 'connected' ? 'bg-green-500 animate-pulse' : isBusy ? 'bg-red-500' : 'bg-amber-400 animate-pulse'}`}></div>
              <span className="text-sm font-semibold text-gray-800">{t('liveChat.callTitle')}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCallMinimized(true)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer" title={t('liveChat.callMinimize')}>
                <i className="ri-arrow-down-s-line text-lg"></i>
              </button>
              <button onClick={handleHangup} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer" title={t('liveChat.callClose')}>
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 px-6 py-6">
            <div className="relative">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isBusy ? 'bg-red-100' : callState === 'connected' ? 'bg-green-100' : 'bg-amber-100'}`}>
                <i className={`text-3xl ${isBusy ? 'ri-phone-off-fill text-red-500' : callState === 'connected' ? 'ri-phone-fill text-green-600' : 'ri-phone-line text-amber-600'}`}></i>
              </div>
              {callState === 'calling' && !isBusy && <div className="absolute inset-0 rounded-full border-4 border-amber-300 animate-ping opacity-60"></div>}
            </div>

            <div className="text-center">
              <p className="font-semibold text-gray-900">{callStateLabel}</p>
              {callState === 'calling' && !isBusy && <p className="text-xs text-amber-500 mt-1">{t('liveChat.callWaiting')}</p>}
              {isBusy && <p className="text-xs text-red-500 mt-1">{t('liveChat.callBusyNote')}</p>}
              {callState === 'connected' && !isBusy && (
                <div className="flex items-center justify-center gap-1.5 mt-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-xs text-green-600 font-medium">{t('liveChat.callLive')}</span>
                </div>
              )}
            </div>

            <button onClick={handleHangup} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center cursor-pointer transition-colors">
              <i className="ri-phone-fill text-xl" style={{ transform: 'rotate(135deg)', display: 'inline-block' }}></i>
            </button>
            {!isBusy && <p className="text-xs text-gray-400 text-center">{t('liveChat.callHangupNote')}</p>}
          </div>
        </div>
      )}

      {/* Voice Call — Minimized bottom bar */}
      {callModalOpen && callMinimized && (
        <div className="fixed bottom-0 left-0 right-0 z-[300] flex items-center gap-3 px-4 py-3 bg-gray-900 text-white" style={{ animation: 'slideDownBar 0.25s ease' }}>
          <button onClick={() => setCallMinimized(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer flex-shrink-0" title={t('liveChat.callExpand')}>
            <i className="ri-arrow-up-s-line text-base"></i>
          </button>

          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isBusy ? 'bg-red-500' : callState === 'connected' ? 'bg-green-500' : 'bg-amber-500'}`}>
            <i className={`text-sm text-white ${callState === 'calling' && !isBusy ? 'ri-phone-line' : 'ri-phone-fill'}`}></i>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold leading-tight">{t('liveChat.callMinBar')}</p>
            <p className={`text-[10px] leading-tight ${isBusy ? 'text-red-400' : callState === 'connected' ? 'text-green-400' : 'text-amber-400'}`}>{callStateLabel}</p>
          </div>

          {callState === 'calling' && !isBusy && (
            <div className="flex items-end gap-[2px] flex-shrink-0">
              {[2, 4, 6, 4, 2].map((h, i) => (
                <div key={i} className="w-[2px] bg-amber-400 rounded-full" style={{ height: `${h}px`, animation: `callBarMin 0.5s ease-in-out infinite ${i * 0.1}s alternate` }}></div>
              ))}
            </div>
          )}

          <button onClick={handleHangup} className="w-9 h-9 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0" title={t('liveChat.callClose')}>
            <i className="ri-phone-fill text-sm" style={{ transform: 'rotate(135deg)', display: 'inline-block' }}></i>
          </button>
        </div>
      )}

      <style>{`
        @keyframes callPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes liveBar {
          from { transform: scaleY(0.5); }
          to { transform: scaleY(1.5); }
        }
        @keyframes slideUpCall {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes slideDownBar {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes callBarMin {
          from { transform: scaleY(0.5); }
          to { transform: scaleY(1.5); }
        }
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  );
}
